// Fictional company "Routeline", a 300-person company building routing and
// warehouse software for logistics firms. See README.md for the schema.
export default {
  id: 'recruiter',
  occupationId: '683b01059a1e027695ece656',
  occupationName: { en: 'Recruiter', ua: 'Рекрутер' },
  format: 'pipeline',
  title: { en: 'A day as a recruiter', ua: 'День рекрутера' },
  tagline: {
    en: 'Four open roles, one impatient engineering lead and a candidate who changes his mind',
    ua: 'Чотири відкриті вакансії, нетерплячий керівник розробки і кандидат, який передумав'
  },
  minutes: 10,
  intro: {
    en: 'You are the in-house recruiter at Routeline, a 300-person company that builds routing and warehouse software for logistics firms. Four roles are open, the senior backend one for 74 days. Hiring managers write to you before you have had coffee.',
    ua: 'Ти внутрішній рекрутер у Routeline, компанії на 300 людей, яка робить софт для маршрутизації та складів логістичних фірм. Відкрито чотири вакансії, сеньйор-бекенд висить уже 74 дні. Наймаючі менеджери пишуть тобі ще до кави.'
  },
  goal: {
    en: 'Move the pipeline without burning candidates or the managers who trust you',
    ua: 'Рухати воронку, не спалюючи кандидатів і менеджерів, які тобі довіряють'
  },
  kpis: [
    { key: 'pipeline', label: { en: 'Pipeline', ua: 'Воронка' }, start: 50 },
    {
      key: 'candidates',
      label: { en: 'Candidate experience', ua: 'Досвід кандидатів' },
      start: 60
    },
    { key: 'managers', label: { en: 'Manager trust', ua: 'Довіра менеджерів' }, start: 55 }
  ],
  competencies: [
    {
      key: 'sourcing',
      label: { en: 'Sourcing', ua: 'Пошук кандидатів' },
      skill: { _id: '85f04e3d438451dc083db880', name: 'Пошук технічних кандидатів' }
    },
    {
      key: 'assessment',
      label: { en: 'Assessment', ua: 'Оцінювання' },
      skill: { _id: '686282c1aa8e5a92e3bc190e', name: "Методологія інтерв'ю" }
    },
    {
      key: 'people',
      label: { en: 'People', ua: 'Робота з людьми' },
      skill: { _id: '686282c1aa8e5a92e3bc1383', name: 'Емоційний інтелект' }
    },
    {
      key: 'process',
      label: { en: 'Process', ua: 'Процес' },
      skill: { _id: '686282c1aa8e5a92e3bc135a', name: 'Організаційні здібності' }
    },
    {
      key: 'ethics',
      label: { en: 'Ethics and law', ua: 'Етика та закон' },
      skill: { _id: '686282c1aa8e5a92e3bc13ce', name: 'Знання трудового законодавства' }
    }
  ],
  people: [
    {
      key: 'eng',
      name: { en: 'Vitalii', ua: 'Віталій' },
      role: { en: 'Head of Engineering', ua: 'Керівник розробки' },
      initials: 'VI',
      color: '#30b0c7'
    },
    {
      key: 'ops',
      name: { en: 'Kateryna', ua: 'Катерина' },
      role: { en: 'Head of Customer Operations', ua: 'Керівниця клієнтського сервісу' },
      initials: 'KA',
      color: '#d5921b'
    },
    {
      key: 'hr',
      name: { en: 'Natalia', ua: 'Наталія' },
      role: { en: 'Head of People', ua: 'Керівниця HR' },
      initials: 'NA',
      color: '#9c27b0'
    },
    {
      key: 'cand',
      name: { en: 'Oksana', ua: 'Оксана' },
      role: { en: 'Candidate, backend developer', ua: 'Кандидатка, бекенд-розробниця' },
      initials: 'OK',
      color: '#19857b'
    }
  ],
  steps: [
    {
      type: 'queue',
      id: 's1',
      at: '09:00',
      comp: ['assessment', 'process'],
      traits: ['C', 'I'],
      text: {
        en: 'Four CVs came in overnight for the four open roles. Open them in the order you think is right and decide on each.',
        ua: 'За ніч на чотири відкриті вакансії прийшли чотири резюме. Відкрий їх у тому порядку, який вважаєш правильним, і прийми рішення по кожному.'
      },
      tickets: [
        {
          id: 't1',
          from: { en: 'Oleksandr M.', ua: 'Олександр М.' },
          urgency: 3,
          sla: { en: 'today', ua: 'сьогодні' },
          subject: { en: 'Senior backend developer', ua: 'Сеньйор бекенд-розробник' },
          body: {
            en: '7 years in Java, 1 year in Go on a warehouse system at a freight company. Led a migration of a monolith to services. Expects the top of the band. Available in one month.',
            ua: '7 років на Java, 1 рік на Go над складською системою у вантажного перевізника. Вів міграцію моноліту на сервіси. Очікує верх вилки. Може вийти за місяць.'
          },
          options: [
            {
              text: {
                en: 'Advance to the technical interview this week. One year of Go on a real system plus the migration is exactly what the routing team lacks.',
                ua: 'Просунути на технічну співбесіду цього тижня. Рік Go на реальній системі плюс міграція — саме те, чого бракує команді маршрутизації.'
              },
              q: 'best',
              fx: { pipeline: 5, managers: 3 },
              fb: {
                en: 'The role has been open 74 days; a candidate who has shipped on Go and led a migration is rare. Waiting costs more than one engineer-hour.',
                ua: 'Вакансія відкрита 74 дні; кандидат, який поставляв на Go і вів міграцію, трапляється рідко. Чекати дорожче за годину інженера.'
              }
            },
            {
              text: {
                en: 'Hold until Vitalii confirms the budget for the top of the band. No point interviewing someone we may not be able to afford.',
                ua: 'Притримати, поки Віталій не підтвердить бюджет на верх вилки. Нема сенсу співбесідувати того, кого, можливо, не потягнемо.'
              },
              q: 'ok',
              fx: { pipeline: -2 },
              fb: {
                en: 'The top of the band is inside the band, so there is nothing to confirm. Every day on hold is a day a competitor can call him.',
                ua: 'Верх вилки — це все ще вилка, підтверджувати нічого. Кожен день у паузі — день, коли йому може подзвонити конкурент.'
              }
            },
            {
              text: {
                en: 'Reject: the posting asks for three years of Go, he has one. Vitalii was clear about the requirements and a weaker profile wastes his time.',
                ua: 'Відмовити: у вакансії три роки Go, у нього один. Віталій чітко назвав вимоги, і слабший профіль лише забирає його час.'
              },
              q: 'bad',
              fx: { pipeline: -6, managers: -2 },
              fb: {
                en: 'Years in a language is a proxy, not the skill. Reading the requirements literally filters out the person the team actually needs.',
                ua: 'Роки з мовою — проксі, а не навичка. Буквальне читання вимог відсіює саме ту людину, яка потрібна команді.'
              }
            }
          ]
        },
        {
          id: 't2',
          from: { en: 'Iryna S.', ua: 'Ірина С.' },
          urgency: 2,
          sla: { en: '2 days', ua: '2 дні' },
          subject: { en: 'Customer support lead', ua: 'Лід служби підтримки' },
          body: {
            en: '4 years in tier-2 support, 2 years leading a team of six at a freight forwarder. No software-company experience. Knows logistics customers well; wrote the onboarding guide at her last job.',
            ua: '4 роки в підтримці другої лінії, 2 роки керувала командою з шести в експедиторській компанії. Досвіду в софтверній компанії немає. Добре знає логістичних клієнтів; на минулому місці писала гайд з онбордингу.'
          },
          options: [
            {
              text: {
                en: 'Advance to a call with Kateryna. Leading a team and knowing the customers matter more here than having worked at a software company.',
                ua: 'Просунути на дзвінок із Катериною. Досвід керування командою і знання клієнтів тут важливіші за роботу в софтверній компанії.'
              },
              q: 'best',
              fx: { pipeline: 4 },
              fb: {
                en: "The team of eight needs a lead who has already managed people and speaks the customers' language. The product can be learned in a month.",
                ua: 'Команді з восьми потрібен лід, який уже керував людьми й говорить мовою клієнтів. Продукт можна вивчити за місяць.'
              }
            },
            {
              text: {
                en: 'Hold and ask her for two references from the forwarder first. A lead role is a risk, and a few days of checks protect the team.',
                ua: 'Притримати і спершу попросити два рекомендаційні контакти з експедитора. Лід — це ризик, і кілька днів перевірок захищають команду.'
              },
              q: 'ok',
              fx: { pipeline: -1, candidates: -2 },
              fb: {
                en: 'References before the first call signal distrust and slow a 30-day-old role. Check them after the interview, when there is something to verify.',
                ua: 'Рекомендації до першого дзвінка сигналізують недовіру і гальмують вакансію, якій 30 днів. Перевіряй після інтерв’ю, коли є що перевіряти.'
              }
            },
            {
              text: {
                en: 'Reject: no SaaS background, and our support works in a ticketing tool she has never used. Keep her in the pool for a tier-2 role.',
                ua: 'Відмовити: без досвіду в SaaS, а наша підтримка працює в тікетній системі, якої вона не бачила. Тримати в базі на другу лінію.'
              },
              q: 'bad',
              fx: { pipeline: -5, candidates: -3 },
              fb: {
                en: 'You screened out on tools, which take a week, and ignored leadership, which takes years. The support team keeps losing people meanwhile.',
                ua: 'Ти відсіяв за інструментами, на які йде тиждень, і проігнорував лідерство, на яке йдуть роки. Підтримка тим часом далі втрачає людей.'
              }
            }
          ]
        },
        {
          id: 't3',
          from: { en: 'Dmytro K.', ua: 'Дмитро К.' },
          urgency: 2,
          sla: { en: '2 days', ua: '2 дні' },
          subject: { en: 'QA automation engineer', ua: 'Інженер з автоматизації тестування' },
          body: {
            en: 'Lists 12 tools and five employers in three years, none longer than eight months. Describes duties, not results. Has a public repository with a small test framework that looks well written.',
            ua: 'Перелічує 12 інструментів і п’ять роботодавців за три роки, жоден не довше восьми місяців. Описує обов’язки, а не результати. Має публічний репозиторій з невеликим тестовим фреймворком, який виглядає охайно.'
          },
          options: [
            {
              text: {
                en: 'Book a 20-minute screening call to ask about the five moves and walk through the repository. Decide after that, not from the CV.',
                ua: 'Призначити 20-хвилинний скринінг, щоб спитати про п’ять переходів і пройтися по репозиторію. Вирішувати після нього, а не з резюме.'
              },
              q: 'best',
              fx: { pipeline: 3 },
              fb: {
                en: 'The pattern is a question, not a verdict. Twenty minutes answers it; the repository shows real skill the CV hides.',
                ua: 'Патерн — це питання, а не вирок. Двадцять хвилин дають відповідь; репозиторій показує реальну навичку, яку резюме ховає.'
              }
            },
            {
              text: {
                en: 'Reject with a short note: five jobs in three years is too risky for a team that needs someone to stay and build the framework.',
                ua: 'Відмовити коротким листом: п’ять робіт за три роки — надто ризиковано для команди, якій потрібна людина, що лишиться й будуватиме фреймворк.'
              },
              q: 'ok',
              fx: { pipeline: -3 },
              fb: {
                en: 'Defensible, but the QA pipeline has one candidate. Two of the five moves might be a bankrupt employer and a contract ending; you did not ask.',
                ua: 'Захистити можна, але у воронці QA один кандидат. Два з п’яти переходів можуть бути банкрутством роботодавця і кінцем контракту; ти не спитав.'
              }
            },
            {
              text: {
                en: 'Advance straight to the two-hour technical interview with the QA lead. The repository is proof enough, and the role has been open 45 days.',
                ua: 'Просунути одразу на двогодинну технічну співбесіду з QA-лідом. Репозиторій — достатній доказ, а вакансія відкрита 45 днів.'
              },
              q: 'bad',
              fx: { managers: -4 },
              fb: {
                en: "Two hours of a lead's time on an unscreened profile is how managers stop trusting your shortlist. Screening is your job, not theirs.",
                ua: 'Дві години ліда на непросіяний профіль — так менеджери перестають довіряти твоєму шортлісту. Скринінг — твоя робота, не їхня.'
              }
            }
          ]
        },
        {
          id: 't4',
          from: { en: 'Maksym R.', ua: 'Максим Р.' },
          urgency: 1,
          sla: { en: 'this week', ua: 'цього тижня' },
          subject: {
            en: 'Applied for QA, profile says sales',
            ua: 'Подався на QA, профіль про продажі'
          },
          body: {
            en: 'Applied to the QA posting. The CV shows six years of B2B sales of transport services, two of them selling to warehouse operators. One line about "wanting to move into IT". The B2B sales role is also open.',
            ua: 'Подався на вакансію QA. У резюме шість років B2B-продажів транспортних послуг, два з них — операторам складів. Один рядок про «бажання перейти в IT». Вакансія B2B-продажів теж відкрита.'
          },
          options: [
            {
              text: {
                en: 'Write to him: the QA role is not a fit, but the sales role is, and here is why. Ask if he wants a call about it this week.',
                ua: 'Написати йому: QA не підходить, а от продажі — так, і ось чому. Спитати, чи хоче дзвінок про це цього тижня.'
              },
              q: 'best',
              fx: { pipeline: 4, candidates: 3 },
              fb: {
                en: 'A misrouted application is a sourcing gift: he already sells to your customers. One honest message turns a rejection into a pipeline.',
                ua: 'Заявка не туди — подарунок для сорсингу: він уже продає твоїм клієнтам. Одне чесне повідомлення перетворює відмову на воронку.'
              }
            },
            {
              text: {
                en: 'Hold in the talent pool and come back to him next week, once the sales manager confirms the profile she wants for the role.',
                ua: 'Притримати в базі й повернутися наступного тижня, коли менеджерка продажів підтвердить, який профіль хоче на роль.'
              },
              q: 'ok',
              fx: { candidates: -1 },
              fb: {
                en: 'Reasonable, but he applied today and will hear nothing for a week. Candidates with options stop waiting around day three.',
                ua: 'Розумно, але він подався сьогодні й тиждень не почує нічого. Кандидати з варіантами перестають чекати десь на третій день.'
              }
            },
            {
              text: {
                en: 'Reject politely: he applied for QA, he is not a QA engineer, and guessing what else he might want is not your job.',
                ua: 'Ввічливо відмовити: подавався на QA, він не QA-інженер, а вгадувати, чого ще він міг би хотіти, — не твоя робота.'
              },
              q: 'bad',
              fx: { pipeline: -3, candidates: -2 },
              fb: {
                en: 'You closed a sales candidate with exactly the market knowledge the sales lead keeps asking for. Reading CVs for fit, not for the job title, is the job.',
                ua: 'Ти закрив кандидата в продажі саме з тим знанням ринку, яке постійно просить лід продажів. Читати резюме на відповідність, а не на назву посади, — і є робота.'
              }
            }
          ]
        }
      ]
    },
    {
      type: 'chat',
      id: 's2',
      at: '09:45',
      comp: ['people', 'process'],
      traits: ['E', 'S'],
      messages: [
        {
          from: 'eng',
          text: {
            en: 'Serhii finished the final round yesterday. Best candidate in six months. He wants €5 000 a month, our band tops out at €4 200. I want to offer him 5 000 today before someone else does.',
            ua: 'Сергій учора пройшов фінал. Найкращий кандидат за пів року. Хоче 228 000 грн на місяць, наша вилка закінчується на 190 000. Хочу сьогодні запропонувати 228 000, поки хтось інший не встиг.'
          }
        },
        {
          from: 'eng',
          text: {
            en: 'Finance will sign if you back it. Can you send the offer by lunch?',
            ua: 'Фінанси підпишуть, якщо ти підтримаєш. Надішлеш офер до обіду?'
          }
        }
      ],
      prompt: { en: 'What do you reply to Vitalii?', ua: 'Що відповідаєш Віталію?' },
      options: [
        {
          text: {
            en: 'He is strong, agreed. Before any number: top of band plus a sign-on bonus and a six-month review usually closes this. I check with Natalia what 5 000 does to the two seniors already on 4 000.',
            ua: 'Так, він сильний. Перш ніж називати цифру: верх вилки плюс бонус за вихід і перегляд за пів року зазвичай закривають таке. Я уточню в Наталії, що 228 000 зробить із двома сеньйорами, які вже на 180 000.'
          },
          q: 'best',
          fx: { managers: 4, pipeline: 2 },
          fb: {
            en: "You kept Vitalii's urgency and swapped a flat salary jump for levers that do not reprice the whole team. Internal equity is the cost he is not seeing.",
            ua: 'Ти зберіг терміновість Віталія і замінив стрибок зарплати важелями, які не переоцінюють усю команду. Внутрішня справедливість — ціна, якої він не бачить.'
          }
        },
        {
          text: {
            en: 'Send it to Natalia and finance as an exception request and wait for their decision. Out-of-band pay is above my level, and I would rather not be the one who approved it.',
            ua: 'Передати Наталії й фінансам як запит на виняток і чекати їхнього рішення. Зарплата поза вилкою — вище мого рівня, і я б не хотів бути тим, хто це погодив.'
          },
          q: 'ok',
          fx: { managers: -2, energy: -2 },
          fb: {
            en: 'Right chain, no judgement. Vitalii asked for your read and got a forwarded email; by the time the committee answers, Serhii may have another offer.',
            ua: 'Ланцюжок правильний, судження немає. Віталій просив твою думку, а отримав пересланий лист; поки комітет відповість, у Сергія може бути інший офер.'
          }
        },
        {
          text: {
            en: 'Send the offer at 5 000 today. Vitalii owns the budget, the role has been open 74 days, and losing the best candidate in six months over 800 a month is a worse outcome.',
            ua: 'Надіслати офер на 228 000 сьогодні. Бюджет у Віталія, вакансія відкрита 74 дні, а втратити найкращого кандидата за пів року через 38 000 на місяць — гірший результат.'
          },
          q: 'bad',
          fx: { managers: 3, pipeline: 3 },
          fb: {
            en: 'Salaries leak within a quarter. One offer above band quietly reprices every senior on the team, and the bill lands on Natalia, not Vitalii.',
            ua: 'Зарплати витікають за квартал. Один офер над вилкою тихо переоцінює кожного сеньйора в команді, і рахунок прилітає Наталії, а не Віталію.'
          },
          later: {
            after: 3,
            note: {
              en: "Two seniors on the routing team found out about Serhii's salary. One has asked Natalia for a 20% raise, the other updated his profile.",
              ua: 'Двоє сеньйорів у команді маршрутизації дізналися про зарплату Сергія. Один попросив у Наталії +20%, другий оновив профіль.'
            },
            fx: { managers: -6, pipeline: -4 }
          }
        },
        {
          text: {
            en: 'Say no: the band is the band, and if we break it once we break it for everyone. Offer 4 200, explain the growth path, and if he declines we keep searching.',
            ua: 'Сказати «ні»: вилка є вилка, і якщо зламати її раз — зламаємо для всіх. Запропонувати 190 000, пояснити шлях зростання, а відмовиться — шукаємо далі.'
          },
          q: 'bad',
          fx: { managers: -6, pipeline: -3 },
          fb: {
            en: 'A rule quoted at a manager under pressure sounds like you do not care about his 74 days. Policies hold when you offer a way through, not a wall.',
            ua: 'Правило, процитоване менеджеру під тиском, звучить так, ніби тобі байдуже до його 74 днів. Політики тримаються, коли ти пропонуєш шлях, а не стіну.'
          }
        }
      ],
      principle: {
        en: 'Hold the band with levers, not with a no.',
        ua: 'Тримай вилку важелями, а не словом «ні».'
      }
    },
    {
      type: 'triage',
      id: 's3',
      at: '10:30',
      comp: ['sourcing', 'process'],
      traits: ['C', 'I'],
      text: {
        en: 'Natalia asks for your sourcing plan for the week. Five roles, and you can actively source for two or three. Order them by where your sourcing hours go first.',
        ua: 'Наталія просить план сорсингу на тиждень. П’ять вакансій, а активно шукати ти можеш на дві-три. Розстав їх за тим, куди твої години сорсингу підуть у першу чергу.'
      },
      items: [
        {
          id: 'backend',
          title: { en: 'Senior backend developer', ua: 'Сеньйор бекенд-розробник' },
          detail: {
            en: 'Open 74 days. Blocks the routing-engine release. Two candidates in process, one of them Oleksandr from this morning.',
            ua: 'Відкрита 74 дні. Блокує реліз движка маршрутизації. Двоє кандидатів у процесі, один з них — ранковий Олександр.'
          }
        },
        {
          id: 'qa',
          title: { en: 'QA automation engineer', ua: 'Інженер з автоматизації тестування' },
          detail: {
            en: 'Open 45 days. Every release is tested by hand, which costs the team a week each time. One candidate in process.',
            ua: 'Відкрита 45 днів. Кожен реліз тестують руками, і це коштує команді тиждень щоразу. Один кандидат у процесі.'
          }
        },
        {
          id: 'support',
          title: { en: 'Customer support lead', ua: 'Лід служби підтримки' },
          detail: {
            en: 'Open 30 days. The team of eight has no lead and lost two people last month. Five candidates in process.',
            ua: 'Відкрита 30 днів. Команда з восьми без ліда, минулого місяця пішло двоє. П’ятеро кандидатів у процесі.'
          }
        },
        {
          id: 'office',
          title: { en: 'Office manager', ua: 'Офіс-менеджер' },
          detail: {
            en: 'Opened yesterday. The current one leaves in six weeks. No candidates yet.',
            ua: 'Відкрита вчора. Теперішня йде за шість тижнів. Кандидатів ще немає.'
          }
        },
        {
          id: 'sales',
          title: { en: 'B2B sales manager', ua: 'Менеджер B2B-продажів' },
          detail: {
            en: 'Open 20 days. Nine candidates in screening. The hiring manager is on holiday for two more weeks.',
            ua: 'Відкрита 20 днів. Дев’ятеро кандидатів на скринінгу. Наймаюча менеджерка у відпустці ще два тижні.'
          }
        }
      ],
      correctOrder: ['backend', 'qa', 'support', 'office', 'sales'],
      explain: {
        en: 'Backend and QA have the thinnest pipelines and the largest cost of staying open: a blocked release and a week lost per release. Support has five candidates, so interviewing beats sourcing there. The office role has six weeks and zero candidates, which is a next-week problem. Sales has nine candidates and nobody to interview them.',
        ua: 'Бекенд і QA мають найтонші воронки й найбільшу ціну простою: заблокований реліз і тиждень втрат на кожен реліз. У підтримки п’ятеро кандидатів, тож там співбесіди важливіші за пошук. Офіс-менеджер має шість тижнів і нуль кандидатів — це проблема наступного тижня. У продажів дев’ятеро кандидатів і нікого, хто б їх співбесідував.'
      },
      fx: { ok: { pipeline: 5, managers: 2 }, fail: { pipeline: -4, energy: -4 } }
    },
    {
      type: 'numeric',
      id: 's4',
      at: '11:30',
      comp: ['process', 'sourcing'],
      traits: ['I', 'C'],
      text: {
        en: 'Vitalii wants two backend offers out within the quarter. Your funnel data from the last 12 months tells you how many screening calls that takes.',
        ua: 'Віталій хоче два офери на бекенд до кінця кварталу. Дані твоєї воронки за останні 12 місяців підказують, скільки для цього потрібно скринінгів.'
      },
      data: [
        { label: { en: 'Offers needed', ua: 'Потрібно оферів' }, value: '2' },
        {
          label: { en: 'Screening → technical interview', ua: 'Скринінг → технічна співбесіда' },
          value: '50%'
        },
        { label: { en: 'Technical → final round', ua: 'Технічна → фінал' }, value: '40%' },
        { label: { en: 'Final → offer', ua: 'Фінал → офер' }, value: '50%' }
      ],
      question: {
        en: 'How many screening calls do you need to produce two offers?',
        ua: 'Скільки скринінг-дзвінків потрібно, щоб отримати два офери?'
      },
      answer: 20,
      tolerance: 0.05,
      unit: { en: 'calls', ua: 'дзвінків' },
      hint: {
        en: 'Multiply the stage rates to get the share of screened candidates who reach an offer, then divide.',
        ua: 'Перемнож конверсії етапів, щоб отримати частку скринінгів, яка доходить до оферу, і поділи.'
      },
      explain: {
        en: '0.5 × 0.4 × 0.5 = 0.1, so one screened candidate in ten reaches an offer. 2 offers ÷ 0.1 = 20 screening calls, roughly two a day for two weeks. That is the number that turns "we are trying" into a plan.',
        ua: '0,5 × 0,4 × 0,5 = 0,1, тобто один скринінг із десяти доходить до оферу. 2 офери ÷ 0,1 = 20 скринінг-дзвінків, приблизно два на день упродовж двох тижнів. Саме ця цифра перетворює «ми стараємось» на план.'
      },
      fx: { ok: { pipeline: 4, managers: 2 }, fail: { pipeline: -3 } }
    },
    {
      type: 'choice',
      id: 's5',
      at: '13:00',
      comp: ['people', 'process'],
      traits: ['S', 'E'],
      text: {
        en: 'Serhii accepted the backend offer on Friday. Today, four days before his start date, he writes: his current employer matched the salary and added a team-lead title, so he is staying. Vitalii does not know yet.',
        ua: 'Сергій прийняв офер на бекенд у п’ятницю. Сьогодні, за чотири дні до виходу, він пише: теперішній роботодавець зрівняв зарплату й додав посаду тімліда, тож він залишається. Віталій ще не знає.'
      },
      options: [
        {
          text: {
            en: 'Thank him, ask one question about what the counter solved, and leave the door open. Then tell Vitalii within the hour, with the status of the next two candidates and a new date for the pipeline.',
            ua: 'Подякувати, спитати одне: що саме закрив контрофер, і лишити двері відчиненими. Потім за годину повідомити Віталія зі статусом двох наступних кандидатів і новою датою для воронки.'
          },
          q: 'best',
          fx: { managers: 4, candidates: 3, pipeline: 2 },
          fb: {
            en: 'A counteroffer is information about your process, so you collect it. Vitalii gets bad news with a plan attached, which is the only form of bad news managers forgive.',
            ua: 'Контрофер — це інформація про твій процес, тож ти її збираєш. Віталій отримує погану новину разом із планом, а це єдина форма поганих новин, яку менеджери прощають.'
          }
        },
        {
          text: {
            en: 'Ask Vitalii whether he will match the counter with a lead title and a raise. Serhii is the best candidate in six months and the role has waited 74 days, so one more round is worth trying.',
            ua: 'Спитати Віталія, чи перекриє він контрофер посадою ліда і підвищенням. Сергій — найкращий кандидат за пів року, а вакансія чекала 74 дні, тож ще одне коло варте спроби.'
          },
          q: 'ok',
          fx: { pipeline: 1, managers: -2, energy: -4 },
          fb: {
            en: 'Bidding wars for someone who already chose to stay rarely end well: about half of counteroffer takers leave within a year anyway, and he has learned his price moves when he pushes.',
            ua: 'Торги за того, хто вже вирішив лишитися, рідко закінчуються добре: близько половини тих, хто бере контрофер, усе одно йдуть за рік, а він навчився, що його ціна рухається, коли він тисне.'
          }
        },
        {
          text: {
            en: 'Tell him that backing out four days before the start is unprofessional and that the company will remember it next time he applies. Then close the file and move on.',
            ua: 'Сказати йому, що відмовлятися за чотири дні до виходу непрофесійно і що компанія це запам’ятає, коли він подасться наступного разу. Потім закрити справу і рухатися далі.'
          },
          q: 'bad',
          fx: { candidates: -8 },
          fb: {
            en: 'He will tell six friends in the same community, and your next backend candidate will have heard the story. Burned bridges are a sourcing cost, not a relief.',
            ua: 'Він розкаже шістьом знайомим у тій самій спільноті, і твій наступний кандидат на бекенд уже чутиме цю історію. Спалені мости — це ціна сорсингу, а не полегшення.'
          }
        },
        {
          text: {
            en: 'Write to Vitalii: "We lost Serhii to a counteroffer, sorry." Then get back to the other roles; there is nothing more to do on this one today.',
            ua: 'Написати Віталію: «Ми втратили Сергія через контрофер, вибач». І повернутися до інших вакансій; по цій сьогодні більше нічого не вдієш.'
          },
          q: 'bad',
          fx: { managers: -6, pipeline: -3 },
          fb: {
            en: 'A manager who hears "we lost him" with no next step hears "you are on your own". The 74 days just became your problem in his eyes.',
            ua: 'Менеджер, який чує «ми його втратили» без наступного кроку, чує «ти сам по собі». 74 дні щойно стали твоєю проблемою в його очах.'
          }
        }
      ],
      principle: {
        en: 'Bad news travels with a plan or not at all.',
        ua: 'Погані новини йдуть разом із планом або не йдуть зовсім.'
      }
    },
    {
      type: 'chat',
      id: 's6',
      at: '14:00',
      comp: ['people', 'assessment'],
      traits: ['S', 'C'],
      messages: [
        {
          from: 'cand',
          text: {
            en: 'Hi. I got the rejection email for the backend role yesterday. Three interviews over four weeks, and the email says "we decided to move forward with other candidates". Could you tell me honestly what went wrong? I want to fix it, not argue.',
            ua: 'Привіт. Учора отримала листа з відмовою по бекенду. Три співбесіди за чотири тижні, а в листі «ми вирішили рухатися далі з іншими кандидатами». Можеш чесно сказати, що пішло не так? Я хочу виправити, а не сперечатися.'
          }
        }
      ],
      prompt: {
        en: 'The panel notes say: strong coding; in the system-design round she went deep on one service and never covered failures or scaling. What do you reply?',
        ua: 'У нотатках панелі: сильний код; у раунді системного дизайну заглибилася в один сервіс і не торкнулася відмов і масштабування. Що відповідаєш?'
      },
      options: [
        {
          text: {
            en: "Pass on the panel's view in usable form: strong coding, but in system design she went deep on one service and skipped failures and scaling. Describe a stronger answer and invite her to talk again in a year.",
            ua: 'Передати думку панелі у придатній формі: код сильний, але в системному дизайні заглибилася в один сервіс і оминула відмови й масштабування. Описати сильнішу відповідь і запросити на розмову за рік.'
          },
          q: 'best',
          fx: { candidates: 8 },
          fb: {
            en: 'Specific, behavioural and about one round, which is feedback she can act on. The invitation to reapply costs nothing and keeps a strong coder in your pool.',
            ua: 'Конкретно, про поведінку і про один раунд — це фідбек, з яким можна працювати. Запрошення повернутися нічого не коштує і тримає сильну розробницю у твоїй базі.'
          }
        },
        {
          text: {
            en: 'Thank her, say the decision was close and the other candidate had more experience with distributed systems, and that she is welcome to apply again. Keep it warm but general.',
            ua: 'Подякувати, сказати, що рішення було близьким і в іншого кандидата більше досвіду з розподіленими системами, і що вона може подаватися знову. Тепло, але загально.'
          },
          q: 'ok',
          fx: { candidates: 2 },
          fb: {
            en: 'Kind and safe, but she asked what to fix and got a comparison. "More experience" is not something she can practise tonight.',
            ua: 'По-доброму й безпечно, але вона спитала, що виправити, а отримала порівняння. «Більше досвіду» — не те, що можна потренувати ввечері.'
          }
        },
        {
          text: {
            en: "Forward the interviewers' raw notes from the design round. She asked for honesty, the notes are honest, and rewriting them would be your opinion, not the panel's.",
            ua: 'Переслати сирі нотатки інтерв’юерів з раунду дизайну. Вона просила чесності, нотатки чесні, а переписувати їх — це вже твоя думка, а не панелі.'
          },
          q: 'bad',
          fx: { candidates: -6, managers: -4 },
          fb: {
            en: 'Interview notes are written for the panel, not the candidate: shorthand, blunt phrases, sometimes names. Sending them exposes the interviewers and often the company.',
            ua: 'Нотатки з інтерв’ю пишуться для панелі, а не для кандидата: скорочення, різкі фрази, іноді імена. Переслати їх — підставити інтерв’юерів, а часто й компанію.'
          }
        },
        {
          text: {
            en: 'Reply that company policy is not to share interview feedback, wish her luck and close the thread. Detailed feedback invites arguments and legal risk.',
            ua: 'Відповісти, що політика компанії — не ділитися фідбеком зі співбесід, побажати удачі й закрити тред. Детальний фідбек запрошує до суперечок і юридичних ризиків.'
          },
          q: 'bad',
          fx: { candidates: -7 },
          fb: {
            en: 'Four weeks of her time ended in a template. She will say so publicly, and the next three candidates for this role will read it before your email.',
            ua: 'Чотири тижні її часу закінчилися шаблоном. Вона скаже про це публічно, і наступні троє кандидатів на цю роль прочитають це раніше за твій лист.'
          }
        }
      ],
      principle: {
        en: 'Feedback is one round, one behaviour, one next step.',
        ua: 'Фідбек — це один раунд, одна поведінка, один наступний крок.'
      }
    },
    {
      type: 'allocate',
      id: 's7',
      at: '15:00',
      comp: ['sourcing', 'process'],
      traits: ['E', 'I'],
      text: {
        en: "You have 100% of next week's sourcing hours for the backend and QA roles. Split them across channels. Think about who actually answers and what each hire costs.",
        ua: 'У тебе 100% годин сорсингу наступного тижня на бекенд і QA. Розподіли їх між каналами. Думай про те, хто насправді відповідає і скільки коштує кожен найм.'
      },
      buckets: [
        {
          id: 'boards',
          label: { en: 'Job boards', ua: 'Джоб-борди' },
          hint: {
            en: 'Cheapest per hire, but senior engineers rarely apply; 90% of responses are junior.',
            ua: 'Найдешевше за найм, але сеньйори майже не відгукуються; 90% відповідей — джуніори.'
          },
          best: 20
        },
        {
          id: 'referrals',
          label: { en: 'Employee referrals', ua: 'Рекомендації працівників' },
          hint: {
            en: 'Half the cost of other channels and the best retention; the bonus has not been announced since spring.',
            ua: 'Удвічі дешевше за інші канали і найкраще утримання; про бонус не нагадували з весни.'
          },
          best: 30
        },
        {
          id: 'outreach',
          label: { en: 'Direct outreach', ua: 'Прямий аутріч' },
          hint: {
            en: 'The only channel where seniors on Go answer; 8% reply rate, so it eats hours.',
            ua: 'Єдиний канал, де відповідають сеньйори на Go; 8% відповідей, тож з’їдає години.'
          },
          best: 40
        },
        {
          id: 'events',
          label: { en: 'Meetups and events', ua: 'Мітапи та події' },
          hint: {
            en: 'Builds the employer brand over a year; no hire this quarter.',
            ua: 'Будує бренд роботодавця за рік; найму цього кварталу не дасть.'
          },
          best: 10
        }
      ],
      explain: {
        en: 'Senior Go engineers are found, not applied for, so outreach gets the biggest share despite the 8% reply rate. Referrals are the cheapest senior channel and only need a reminder to the team. Boards still fill the QA funnel; events are a long game and get a token hour.',
        ua: 'Сеньйорів на Go знаходять, а не чекають на їхній відгук, тому аутріч отримує найбільшу частку попри 8% відповідей. Рекомендації — найдешевший канал для сеньйорів, і їм потрібне лише нагадування команді. Борди все ще наповнюють воронку QA; події — довга гра, і їм дістається символічна година.'
      },
      fx: { ok: { pipeline: 6 }, fail: { pipeline: -4 } }
    },
    {
      type: 'chat',
      id: 's8',
      at: '16:00',
      comp: ['ethics', 'people'],
      traits: ['E', 'S'],
      messages: [
        {
          from: 'ops',
          text: {
            en: 'About the support lead shortlist. The shifts run until 22:00, so please filter out women with small children, they will be off sick all the time. And nobody over 45, they do not learn the new tooling. Saves us both time.',
            ua: 'Щодо шортліста на ліда підтримки. Зміни до 22:00, тому відсій, будь ласка, жінок із малими дітьми, вони постійно будуть на лікарняних. І нікого за 45, вони не вчать нові інструменти. Збережемо час обом.'
          }
        }
      ],
      prompt: { en: 'What do you reply?', ua: 'Що відповідаєш?' },
      options: [
        {
          text: {
            en: 'Say no: age and family status cannot be criteria, that is discrimination under labour law and the company carries the risk. Instead, put the 22:00 shifts in the ad and ask every candidate about availability and how they learn new tools.',
            ua: 'Відмовити: вік і сімейний стан не можуть бути критеріями, це дискримінація за трудовим правом, і ризик несе компанія. Натомість вказати зміни до 22:00 в оголошенні й питати кожного про доступність і освоєння нових інструментів.'
          },
          q: 'best',
          fx: { managers: 2, candidates: 4 },
          fb: {
            en: "Kateryna's real worry is evening coverage and learning speed. You refused the proxy and gave her a lawful way to test the actual thing, so she loses nothing.",
            ua: 'Справжня тривога Катерини — вечірнє покриття і швидкість навчання. Ти відмовив проксі й дав їй законний спосіб перевірити саме це, тож вона нічого не втрачає.'
          }
        },
        {
          text: {
            en: "Escalate to Natalia right away and let her handle Kateryna. A request like this is above a recruiter's pay grade, it should be documented by HR, and I would rather not argue with a department head.",
            ua: 'Одразу ескалювати Наталії, і нехай вона розбирається з Катериною. Такий запит — вище рівня рекрутера, HR має його задокументувати, і я б не хотів сперечатися з керівницею відділу.'
          },
          q: 'ok',
          fx: { managers: -4 },
          fb: {
            en: 'Documenting it is right, but skipping the conversation turns a manager you work with weekly into someone reported behind her back. Say no first, then tell Natalia.',
            ua: 'Задокументувати правильно, але оминути розмову означає перетворити менеджерку, з якою ти працюєш щотижня, на ту, на кого поскаржилися за спиною. Спершу скажи «ні», потім повідом Наталію.'
          }
        },
        {
          text: {
            en: 'Agree quietly and apply the filter without writing it down anywhere. She is the hiring manager, she knows what the team needs, and nobody will see the shortlist criteria anyway.',
            ua: 'Тихо погодитися й застосувати фільтр, ніде його не записуючи. Вона наймаюча менеджерка, вона знає, що потрібно команді, а критерії шортліста все одно ніхто не побачить.'
          },
          q: 'bad',
          fx: { candidates: -10, managers: 2 },
          fb: {
            en: 'Discrimination done quietly is still discrimination, and you are the one who applied it. Unlawful filters leak through rejected candidates, not through documents.',
            ua: 'Тиха дискримінація — все одно дискримінація, і застосував її ти. Незаконні фільтри витікають через відхилених кандидатів, а не через документи.'
          },
          later: {
            after: 1,
            note: {
              en: 'A candidate over 45 noticed she was dropped right after the screening call and asked in writing why. Natalia is now involved.',
              ua: 'Кандидатка за 45 помітила, що її зняли одразу після скринінгу, і письмово спитала чому. Тепер цим займається Наталія.'
            },
            fx: { managers: -8 }
          }
        },
        {
          text: {
            en: 'Tell her the shortlist is hers to judge: you will send all suitable CVs and she can decide whom to interview using whatever criteria she wants. That keeps you out of it.',
            ua: 'Сказати їй, що шортліст — на її розсуд: ти надішлеш усі підходящі резюме, а вона сама вирішить, кого кликати, за якими завгодно критеріями. Так ти ні при чому.'
          },
          q: 'bad',
          fx: { candidates: -6, managers: -2 },
          fb: {
            en: 'Handing the filter down the line does not make it lawful, and you knew the criteria. "Not my decision" is the sentence that ends recruiter careers in a tribunal.',
            ua: 'Передати фільтр далі по ланцюжку не робить його законним, і ти знав критерії. «Це не моє рішення» — фраза, якою закінчуються кар’єри рекрутерів у суді.'
          }
        }
      ],
      principle: {
        en: 'Refuse the proxy, test the real requirement.',
        ua: 'Відмовляй проксі, перевіряй справжню вимогу.'
      }
    },
    {
      type: 'spot',
      id: 's9',
      at: '17:15',
      comp: ['ethics', 'assessment'],
      traits: ['C'],
      text: {
        en: 'Kateryna sends a draft job ad for the support lead to publish tonight. It has 3 mistakes. Tap each one.',
        ua: 'Катерина надсилає чернетку оголошення на ліда підтримки, щоб опублікувати ввечері. У ній 3 помилки. Натисни на кожну.'
      },
      mock: {
        kind: 'card',
        title: {
          en: 'Job ad draft: Customer support lead',
          ua: 'Чернетка вакансії: Лід служби підтримки'
        },
        blocks: [
          {
            id: 'b1',
            kind: 'heading',
            label: { en: 'Customer support lead', ua: 'Лід служби підтримки' },
            value: ''
          },
          { id: 'b2', kind: 'field', label: { en: 'Company', ua: 'Компанія' }, value: 'Routeline' },
          {
            id: 'b3',
            kind: 'field',
            label: { en: 'Schedule', ua: 'Графік' },
            value: { en: 'Mon–Fri, 09:00–18:00', ua: 'Пн–Пт, 09:00–18:00' },
            issue: {
              en: 'The team works shifts until 22:00; candidates will find out at the interview and drop out.',
              ua: 'Команда працює змінами до 22:00; кандидати дізнаються про це на співбесіді й відпадуть.'
            }
          },
          {
            id: 'b4',
            kind: 'field',
            label: { en: 'Requirements', ua: 'Вимоги' },
            value: {
              en: '3+ years in support, team leadership, age 25–35',
              ua: '3+ роки в підтримці, керування командою, вік 25–35'
            },
            issue: {
              en: 'An age limit in a job ad is unlawful discrimination and a fine for the company.',
              ua: 'Вікове обмеження в оголошенні — незаконна дискримінація і штраф для компанії.'
            }
          },
          {
            id: 'b5',
            kind: 'field',
            label: { en: 'Nice to have', ua: 'Буде плюсом' },
            value: {
              en: 'Logistics background, English B2',
              ua: 'Досвід у логістиці, англійська B2'
            }
          },
          {
            id: 'b6',
            kind: 'line',
            label: { en: 'Salary', ua: 'Зарплата' },
            value: {
              en: 'From €1 800, discussed at interview',
              ua: 'Від 80 000 грн, обговорюється на співбесіді'
            }
          },
          {
            id: 'b7',
            kind: 'field',
            label: { en: 'Apply by', ua: 'Подаватися до' },
            value: '31.09.2026',
            issue: {
              en: 'September has 30 days; the ad would show a date that does not exist.',
              ua: 'У вересні 30 днів; в оголошенні стояла б дата, якої не існує.'
            }
          },
          {
            id: 'b8',
            kind: 'note',
            label: { en: 'How to apply', ua: 'Як податися' },
            value: {
              en: 'Send your CV through the careers page',
              ua: 'Надішліть резюме через сторінку вакансій'
            }
          },
          { id: 'b9', kind: 'button', label: { en: 'Publish', ua: 'Опублікувати' }, value: '' }
        ]
      },
      explain: {
        en: 'The age range is the serious one: it is unlawful and visible to everyone. The schedule hides the 22:00 shifts, which wastes candidates and interviews. The deadline is a date that does not exist. Everything else can go out as written.',
        ua: 'Вік — найсерйозніша: це незаконно й видно всім. Графік ховає зміни до 22:00, а це марнує кандидатів і співбесіди. Дедлайн — дата, якої не існує. Решта може йти як є.'
      },
      fx: { ok: { candidates: 5, managers: 3 }, fail: { candidates: -5 } }
    }
  ],
  verdict: {
    bands: [
      {
        min: 0.8,
        title: {
          en: 'You could run this desk tomorrow',
          ua: 'Ти міг би вести цей стіл уже завтра'
        },
        text: {
          en: 'You screened for fit instead of titles, held the band with levers and refused the unlawful filter without losing the manager. That is senior recruiting.',
          ua: 'Ти оцінював за відповідністю, а не за назвами посад, тримав вилку важелями і відмовив незаконному фільтру, не втративши менеджерку. Це рекрутинг рівня сеньйора.'
        }
      },
      {
        min: 0.6,
        title: {
          en: 'Solid recruiter with a few slow reflexes',
          ua: 'Впевнений рекрутер із кількома повільними рефлексами'
        },
        text: {
          en: 'Most calls were right; the misses were about speed, telling managers and candidates later than they needed to hear it.',
          ua: 'Більшість рішень правильні; промахи стосувались швидкості: ти казав менеджерам і кандидатам пізніше, ніж їм треба було почути.'
        }
      },
      {
        min: 0.4,
        title: {
          en: 'You would fill roles, but leave bruises',
          ua: 'Ти б закривав вакансії, але лишав синці'
        },
        text: {
          en: 'You tend to optimise for the manager in front of you and forget the candidate who is not in the room. Re-read the feedback on the two candidate chats.',
          ua: 'Ти схильний оптимізувати під менеджера перед собою і забувати кандидата, якого немає в кімнаті. Перечитай фідбек до двох чатів із кандидатами.'
        }
      },
      {
        min: 0,
        title: { en: 'This desk would have buried you', ua: 'Цей стіл би тебе поховав' },
        text: {
          en: 'Many choices bought peace today at the cost of next month. Recruiting is the opposite: say the costly thing early and keep the door open.',
          ua: 'Багато рішень купували спокій сьогодні ціною наступного місяця. Рекрутинг — навпаки: кажи дороге рано і тримай двері відчиненими.'
        }
      }
    ],
    reality: [
      {
        en: 'Roughly a third of the day is screening calls that lead nowhere: eight to ten a day, one or two worth a second conversation.',
        ua: 'Приблизно третина дня — скринінг-дзвінки, які нікуди не ведуть: вісім-десять на день, один-два варті другої розмови.'
      },
      {
        en: 'You decide almost nothing yourself. Managers choose, candidates choose; your job is keeping both honest and on time.',
        ua: 'Сам ти майже нічого не вирішуєш. Обирають менеджери, обирають кандидати; твоя робота — тримати обох чесними і вчасними.'
      },
      {
        en: 'Once or twice a month someone asks you for a filter you cannot apply. Saying no without losing them is a skill you practise, not a policy you quote.',
        ua: 'Раз чи двічі на місяць хтось просить фільтр, який ти не можеш застосувати. Відмовити, не втративши людину, — навичка, яку тренуєш, а не політика, яку цитуєш.'
      }
    ]
  }
};
